/**
 * Vertrag „Dokument der Marke (PDF)“ (0071): Die Marke schickt ihren eigenen Vertrag als PDF. Unterschrieben wird eine
 * Unterschriftsseite, die das PDF mit Datei, Größe und Prüfwert (SHA-256) benennt; der Versand friert Seite und Prüfwert
 * ein. Ergebnis ist das unveränderte Original plus ein Unterschriftenprotokoll als eigenes PDF (Seite mit allen
 * Unterschriften und Bildern, Prüfwerte, Ereignisse mit Zeitpunkten). Einfache elektronische Signatur mit Protokoll,
 * keine qualifizierte. Rein, getestet in packages/domain/test/contract-document.test.ts.
 */
import { renderContractPdf, type ContractPdfModel, type ContractPdfSignature } from "./contract-pdf.js";
import type { ContractRole, CustomVariableDef } from "./contract-variables.js";
import type { Language } from "./types.js";

/** Eigene Angaben der Unterschriftsseite; die Werte kommen aus der Datei, nie aus einer Eingabe. */
export const DOCUMENT_COVER_VARIABLES: readonly CustomVariableDef[] = [
  { key: "document_name", label: { de: "Datei", en: "File" }, type: "text", required: true },
  { key: "document_size", label: { de: "Größe", en: "Size" }, type: "text", required: true },
  { key: "document_sha256", label: { de: "Prüfwert (SHA-256)", en: "Checksum (SHA-256)" }, type: "text", required: true },
];

/** Höchstgröße des PDFs einer Marke (wie alle Anhänge). */
export const DOCUMENT_MAX_BYTES = 15 * 1024 * 1024;

const ROLE_ORDER: readonly ContractRole[] = ["brand", "talent", "agency"];

/** Größe lesbar in der Sprache des Vertrags: „2,4 MB“ bzw. „2.4 MB“, unter 1 MB in KB. */
export function formatFileSize(bytes: number, language: Language): string {
  const b = Math.max(0, Math.round(bytes));
  const locale = language === "en" ? "en-GB" : "de-DE";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.max(1, Math.round(b / 1024)))} KB`;
  return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(b / (1024 * 1024))} MB`;
}

/** Werte der Unterschriftsseite aus der angemeldeten Datei. */
export function documentCoverValues(doc: { name: string; size: number; sha256: string }, language: Language): Record<string, string> {
  return { document_name: doc.name, document_size: formatFileSize(doc.size, language), document_sha256: doc.sha256.toLowerCase() };
}

const COVER = {
  de: {
    head: "<h1>{{contract.title}}</h1>\n<p>Vertragsnummer {{contract.number}} · Datum {{contract.date|date}}</p>",
    doc: "<h2>1. Dokument</h2>\n<p>Gegenstand dieser Unterschrift ist das folgende Dokument{{#if brand.legal_name}} von {{brand.legal_name}}{{/if}}. Es liegt als PDF-Datei unverändert bei und bildet mit dieser Unterschriftsseite eine Einheit.</p>\n<ul>\n  <li>Datei: {{var.document_name}}</li>\n  <li>Größe: {{var.document_size}}</li>\n  <li>Prüfwert (SHA-256): {{var.document_sha256}}</li>\n</ul>\n<p>Der Prüfwert ist der digitale Fingerabdruck der Datei: Jede Änderung an der Datei ergibt einen anderen Prüfwert.</p>",
    parties: "<h2>2. Parteien</h2>",
    brand: "{{#if brand.legal_name}}<p><strong>{{brand.legal_name}}</strong>{{#if brand.address_block}}<br>{{brand.address_block}}{{/if}}{{#if brand.represented_by}}<br>vertreten durch {{brand.represented_by}}{{/if}}<br>(„Marke“)</p>{{/if}}",
    talent: "{{#if talent.full_name}}<p><strong>{{talent.full_name}}</strong>{{#if talent.address_block}}<br>{{talent.address_block}}{{/if}}{{#if talent.is_minor}}<br>gesetzlich vertreten durch {{talent.guardian_names}}{{/if}}<br>(„Creator“)</p>{{/if}}",
    agency: "<p><strong>{{agency.legal_name}}</strong><br>{{agency.address_block}}<br>vertreten durch {{agency.represented_by}}<br>(„Agentur“)</p>",
    agencyOnly: "<p>Die Unterschriften holt {{agency.legal_name}} ein und bewahrt dieses Protokoll auf.</p>",
    sign: "<h2>3. Unterschrift</h2>\n<p>Mit ihrer Unterschrift bestätigen die Unterzeichner, das oben bezeichnete Dokument vollständig gelesen zu haben, und erklären ihr Einverständnis mit seinem Inhalt.</p>\n<p>Unterschrieben wird mit einer einfachen elektronischen Signatur; Zeitpunkte, Prüfwerte und weitere Nachweise stehen im Unterschriftenprotokoll. Es handelt sich nicht um eine qualifizierte elektronische Signatur. Verlangt das Gesetz für das Dokument eine strengere Form, genügt diese Unterschrift nicht.</p>",
  },
  en: {
    head: "<h1>{{contract.title}}</h1>\n<p>Contract number {{contract.number}} · Date {{contract.date|date}}</p>",
    doc: "<h2>1. Document</h2>\n<p>This signature relates to the following document{{#if brand.legal_name}} from {{brand.legal_name}}{{/if}}. It is attached unchanged as a PDF file and forms a single unit with this signature page.</p>\n<ul>\n  <li>File: {{var.document_name}}</li>\n  <li>Size: {{var.document_size}}</li>\n  <li>Checksum (SHA-256): {{var.document_sha256}}</li>\n</ul>\n<p>The checksum is the digital fingerprint of the file: any change to the file results in a different checksum.</p>",
    parties: "<h2>2. Parties</h2>",
    brand: "{{#if brand.legal_name}}<p><strong>{{brand.legal_name}}</strong>{{#if brand.address_block}}<br>{{brand.address_block}}{{/if}}{{#if brand.represented_by}}<br>represented by {{brand.represented_by}}{{/if}}<br>(the \"Brand\")</p>{{/if}}",
    talent: "{{#if talent.full_name}}<p><strong>{{talent.full_name}}</strong>{{#if talent.address_block}}<br>{{talent.address_block}}{{/if}}{{#if talent.is_minor}}<br>represented by their legal guardian(s) {{talent.guardian_names}}{{/if}}<br>(the \"Creator\")</p>{{/if}}",
    agency: "<p><strong>{{agency.legal_name}}</strong><br>{{agency.address_block}}<br>represented by {{agency.represented_by}}<br>(the \"Agency\")</p>",
    agencyOnly: "<p>The signatures are collected by {{agency.legal_name}}, which keeps this record.</p>",
    sign: "<h2>3. Signature</h2>\n<p>By signing, the signatories confirm that they have read the document identified above in full and declare their agreement with its content.</p>\n<p>Signing takes place with a simple electronic signature; times, checksums and further evidence are listed in the signature record. It is not a qualified electronic signature. Where the law requires a stricter form for the document, this signature does not suffice.</p>",
  },
} as const;

/**
 * Unterschriftsseite als Vorlage (Platzhalter wie jede Vorlage, gerendert mit dem Vertragskontext und
 * DOCUMENT_COVER_VARIABLES). Unterschriftsfelder genau der unterschreibenden Parteien, zuletzt, ohne Text danach.
 */
export function documentCoverTemplate(language: Language, roles: readonly ContractRole[]): string {
  const t = COVER[language === "en" ? "en" : "de"];
  const has = (r: ContractRole) => roles.includes(r);
  return [
    t.head, t.doc, t.parties,
    has("brand") ? t.brand : "", has("talent") ? t.talent : "", has("agency") ? t.agency : t.agencyOnly,
    t.sign,
    ...ROLE_ORDER.filter(has).map((r) => `{{signature:${r}}}`),
  ].filter(Boolean).join("\n");
}

// ── Unterschriftenprotokoll als PDF ───────────────────────────────────────────────────────────────────────────────

export interface SignatureProtocolRow { at: string; event: string; person: string; evidence: string }

export interface SignatureProtocolInput {
  language: Language;
  /** Titel des Vertrags */
  title: string;
  numberLabel: string | null;
  agencyName: string;
  document: { name: string; size: number | null; sha256: string };
  /** Unterschriftsseite, gerendert und bereinigt (enthält die Unterschriftsfelder) */
  coverHtml: string;
  signatures: ContractPdfSignature[];
  /** Zeilen der Beweiskette, schon lesbar (Zeitpunkt in der Zeitzone der Agentur) */
  rows: SignatureProtocolRow[];
  /** Zeitzone für die Spaltenüberschrift */
  timeZone: string;
  /** lesbare Zeitpunkte */
  sentAt: string | null;
  completedAt: string | null;
  /** Prüfwert der Unterschriftsseite beim Versand und bei Abschluss */
  documentSha: string | null;
  finalSha: string | null;
  /** Hinweis oben (Aufhebung, Kündigung, Zwischenstand) */
  banner?: string | null;
}

export const SIGNATURE_PROTOCOL_TEXT: Record<Language, {
  title: string; fassung: string; intro: (title: string, number: string, sent: string, done: string | null) => string;
  original: string; cover: string; final: string; cols: [string, string, string, string]; how: string;
  /** Art der Signatur und Prüfung, wenn alle elektronisch unterschrieben haben */
  howText: string;
  /** Zusatz nach dem ersten Satz, sobald jemand auf Papier unterschrieben hat (dort gibt es keine Lesebestätigung) */
  paperNote: string;
}> = {
  de: {
    title: "Unterschriftenprotokoll",
    fassung: "Unterschriftenprotokoll",
    intro: (t, n, s, d) => `Protokoll zum Vertrag „${t}“, Nummer ${n}, versendet am ${s}${d ? `, vollständig unterschrieben am ${d}` : ""}. Das Original ist das PDF der Marke; es wird unverändert mit diesem Protokoll aufbewahrt.`,
    original: "SHA-256 des Originals (PDF der Marke)",
    cover: "SHA-256 der Unterschriftsseite beim Versand",
    final: "SHA-256 bei Abschluss",
    cols: ["Zeitpunkt", "Ereignis", "Person", "Nachweis"],
    how: "Art der Signatur und Prüfung",
    howText: "Einfache elektronische Signatur mit Protokoll: Jede Person hat vor der Unterschrift das Original geöffnet und bestätigt, es gelesen zu haben; unterschrieben hat sie per persönlichem Link oder im Portal mit Namen und, soweit angegeben, gezeichneter Unterschrift. Das ist keine fortgeschrittene oder qualifizierte elektronische Signatur im Sinne der eIDAS-Verordnung; verlangt das Gesetz eine strengere Form, genügt sie nicht. So prüfen Sie das Original: den SHA-256-Wert der PDF-Datei berechnen (etwa mit „shasum -a 256 Datei.pdf“ oder „certutil -hashfile Datei.pdf SHA256“) und mit dem Wert oben vergleichen. Stimmen beide überein, ist es genau die unterschriebene Datei.",
    paperNote: "Ausgenommen sind Personen, die auf Papier unterschrieben haben: Bei ihnen gibt es keine Lesebestätigung im Portal, Beleg ist das eingescannte Papier.",
  },
  en: {
    title: "Signature record",
    fassung: "Signature record",
    intro: (t, n, s, d) => `Record for the contract "${t}", number ${n}, sent on ${s}${d ? `, fully signed on ${d}` : ""}. The original is the brand's PDF; it is kept unchanged together with this record.`,
    original: "SHA-256 of the original (brand's PDF)",
    cover: "SHA-256 of the signature page at sending",
    final: "SHA-256 at completion",
    cols: ["Time", "Event", "Person", "Evidence"],
    how: "Type of signature and verification",
    howText: "Simple electronic signature with audit record: before signing, each person opened the original and confirmed having read it; they signed via a personal link or in the portal with their name and, where given, a drawn signature. This is not an advanced or qualified electronic signature within the meaning of the eIDAS Regulation; where the law requires a stricter form, it does not suffice. To verify the original, compute the SHA-256 value of the PDF file (for example with \"shasum -a 256 file.pdf\" or \"certutil -hashfile file.pdf SHA256\") and compare it with the value above. If both match, it is exactly the signed file.",
    paperNote: "This does not apply to persons who signed on paper: there is no read confirmation in the portal for them; the scanned paper is the evidence.",
  },
};

/** Modell des Unterschriftenprotokolls für den PDF-Schreiber (renderContractPdf). */
export function signatureProtocolModel(input: SignatureProtocolInput): ContractPdfModel {
  const lang: Language = input.language === "en" ? "en" : "de";
  const t = SIGNATURE_PROTOCOL_TEXT[lang];
  const number = input.numberLabel ?? "–";
  const hashes = [
    `${t.original}: ${input.document.sha256.toLowerCase()}`,
    input.documentSha ? `${t.cover}: ${input.documentSha}` : null,
    input.finalSha ? `${t.final}: ${input.finalSha}` : null,
  ].filter((x): x is string => !!x);
  return {
    language: lang,
    title: `${t.title} · ${input.title}${input.numberLabel ? ` · ${input.numberLabel}` : ""}`,
    header: `${input.agencyName} · ${number} · ${t.fassung}`,
    banner: input.banner ?? null,
    bodyHtml: input.coverHtml,
    annexHtml: null,
    signatures: input.signatures,
    protocol: {
      title: t.title,
      intro: t.intro(input.title, number, input.sentAt ?? "–", input.completedAt),
      hashes,
      columns: [`${t.cols[0]} (${input.timeZone})`, t.cols[1], t.cols[2], t.cols[3]],
      rows: input.rows,
      how: { title: t.how, text: signatureHowText(lang, input.signatures.some((x) => x.signed && x.paper)) },
    },
  };
}

/** Art der Signatur und Prüfung; mit Papierunterschrift folgt dem ersten Satz der Zusatz, dass für sie das Papier der Beleg ist. */
export function signatureHowText(language: Language, paper: boolean): string {
  const t = SIGNATURE_PROTOCOL_TEXT[language === "en" ? "en" : "de"];
  if (!paper) return t.howText;
  const cut = t.howText.indexOf(". ") + 1;
  return `${t.howText.slice(0, cut)} ${t.paperNote}${t.howText.slice(cut)}`;
}

/** Unterschriftenprotokoll als PDF-Datei. `now` nur für reproduzierbare Tests. */
export function renderSignatureProtocolPdf(input: SignatureProtocolInput, opts: { now?: Date } = {}): Uint8Array {
  return renderContractPdf(signatureProtocolModel(input), opts);
}

/** Beginnt die Datei wie ein PDF („%PDF-“)? Die Oberfläche prüft das vor dem Hochladen. */
export function looksLikePdf(bytes: Uint8Array): boolean {
  return bytes.length >= 5 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d;
}
